/* eslint-disable react/no-unused-prop-types */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Text from "./Text.js";
import Flex from "./Flex.js";
import Theme from "./Theme.js";
import IconUtil from "./IconUtil.js";
import S from './ProgressBar.css';
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
const COMPONENT_NAME = 'ProgressBar';
export default function ProgressBar(props) {
  const {
    variant
  } = props;
  const theme = useContext(Theme.Context);
  const {
    completed,
    total = 100,
    customLabel,
    completeLabelVisible: pCompleteLabelVisible,
    customCompleteLabel: pCustomCompleteLabel,
    completeLabelVariant,
    completeLabelClassName,
    className,
    barClassName,
    labelVariant,
    labelClassName,
    labelVisible: pLabelVisible,
    icon: pIcon,
    iconOpts,
    imageOpts,
    iconComplete,
    iconCompleteOpts,
    imageCompleteOpts,
    iconVisible
  } = theme.props(COMPONENT_NAME, variant, props);
  const cn = theme.classes(COMPONENT_NAME, 'progressBar', variant, className, S.channel);
  const {
    style,
    label,
    icon,
    labelVisible,
    indeterminate
  } = useProgressBar({
    total,
    completed,
    customLabel,
    labelClassName,
    labelVariant,
    iconComplete,
    imageCompleteOpts,
    imageOpts,
    iconCompleteOpts,
    iconOpts,
    icon: pIcon,
    customCompleteLabel: pCustomCompleteLabel,
    completeLabelVariant,
    completeLabelClassName,
    theme,
    labelVisible: pLabelVisible,
    completeLabelVisible: pCompleteLabelVisible
  });
  const bcn = theme.subclasses(COMPONENT_NAME, 'bar', barClassName, S.bar, indeterminate && S.indeterminate);
  return /*#__PURE__*/_jsx(Flex, {
    align: "center",
    gap: 2,
    children: /*#__PURE__*/_jsx("div", {
      className: cn,
      children: /*#__PURE__*/_jsx("div", {
        className: bcn,
        style: style,
        children: /*#__PURE__*/_jsxs(Flex, {
          variant: "rowS",
          align: "center",
          children: [labelVisible && label, iconVisible && icon]
        })
      })
    })
  });
}
ProgressBar.propTypes = {
  /**
   * The number of items completed out of the total. Will be used to generate a progress 
   * bar percentage using (completed / total)
   */
  completed: PropTypes.number,
  /**
   * The total number of items to complete represented by the progress bar.
   */
  total: PropTypes.number,
  /**
   * A custom node or function to render as the progress bar label (in place of a percentage display).
   * 
   */
  customLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * className property to pass to outer container div of progress bar
   */
  className: PropTypes.string,
  /**
   * className property to pass to innter bar div of progress bar
   */
  barClassName: PropTypes.string,
  /**
   * Text component variant for percentage label
   */
  labelVariant: PropTypes.string,
  /**
   * className property to pass to label Text component
   */
  labelClassName: PropTypes.string,
  /**
   * Boolean indicating whether the progress label should be shown
   */
  labelVisible: PropTypes.bool,
  /**
   * A custom node to render on the progress bar when the progress perventage is 100% (or higher)
   */
  customCompleteLabel: PropTypes.node,
  /**
   * Text component variant for complete label
   */
  completeLabelVariant: PropTypes.string,
  /**
   * className property to pass to complete label Text component
   */
  completeLabelClassName: PropTypes.string,
  /**
   * Boolean indicating whether the complete label should be shown
   */
  completeLabelVisible: PropTypes.bool,
  /**
   * Icon class name or url to display on the progress bar
   */
  icon: PropTypes.string,
  /**
   * Options for the custom icon's apperance when a font class name
   */
  iconOpts: PropTypes.object,
  /**
   * Options for the custom icon's apperance when an image url
   */
  imageOpts: PropTypes.object,
  /**
   * Icon class name or url to display on the progress bar when complete
   */
  iconComplete: PropTypes.string,
  /**
   * Options for the complete icon's apperance when a font class name
   */
  iconCompleteOpts: PropTypes.object,
  /**
   * Options for the complete icon's apperance when an image url
   */
  imageCompleteOpts: PropTypes.object,
  /**
   * Whether the icon should be shown
   */
  iconVisible: PropTypes.bool
};
ProgressBar.defaultProps = {
  completed: undefined,
  total: 100,
  customLabel: undefined,
  className: undefined,
  barClassName: undefined,
  labelVariant: undefined,
  labelClassName: undefined,
  labelVisible: true,
  customCompleteLabel: undefined,
  completeLabelVariant: undefined,
  completeLabelClassName: undefined,
  completeLabelVisible: false,
  icon: undefined,
  iconOpts: undefined,
  imageOpts: undefined,
  iconComplete: undefined,
  iconCompleteOpts: undefined,
  imageCompleteOpts: undefined,
  iconVisible: true
};
function useProgressBar(opts) {
  const {
    total,
    customLabel,
    labelVariant,
    labelClassName,
    iconComplete,
    imageCompleteOpts,
    imageOpts,
    iconCompleteOpts,
    customCompleteLabel,
    completeLabelVariant,
    completeLabelClassName,
    iconOpts,
    icon,
    theme,
    completeLabelVisible
  } = opts;
  let {
    completed,
    labelVisible
  } = opts;
  const indeterminate = completed == null;
  completed = indeterminate ? 50 : completed;
  const pct = Math.floor(Math.min(completed / total * 100, 100));
  const pctStr = `${pct}%`;
  const style = {
    width: pctStr
  };
  const isComplete = pct === 100;
  let label = customLabel || (indeterminate ? null : pctStr);
  if (typeof label === 'function') {
    label = label(pct);
  }
  const completeLabel = typeof customCompleteLabel === 'string' ? /*#__PURE__*/_jsx(Text, {
    variant: completeLabelVariant || labelVariant,
    className: completeLabelClassName || labelClassName,
    children: customCompleteLabel
  }) : customCompleteLabel;
  label = typeof label === 'string' ? /*#__PURE__*/_jsx(Text, {
    style: {
      whiteSpace: 'nowrap'
    },
    variant: labelVariant,
    className: labelClassName,
    children: label
  }) : label;
  let icn = null;
  if (isComplete && iconComplete) {
    icn = IconUtil.create(iconComplete, imageCompleteOpts || imageOpts, iconCompleteOpts || iconOpts, theme);
  } else if (icon) {
    icn = IconUtil.create(icon, imageOpts, iconOpts, theme);
  }
  if (isComplete && completeLabel) {
    label = completeLabel;
  }
  labelVisible = labelVisible || completeLabelVisible && isComplete;
  return {
    style,
    label,
    icon: icn,
    isComplete,
    indeterminate,
    customCompleteLabel: completeLabel,
    labelVisible
  };
}